
import React from 'react';
import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import WarningIcon from 'assets/main/warning.svg';
import {
  ListContainer,
  WorkerCard,
  WorkerHeader,
  WorkerName,
  WorkerId,
  StatusChip,
  DurationText,
  StyledLinearProgress,
  TimeContainer,
  TimeLabel,
  TimeValue,
  CountdownBox,
  CountdownText,
} from './styles';
import { IFacilityShift } from 'modules/facilityShifts/types';
import SecureAvatar from 'components/common/SecureAvatar';

interface AssignedWorkerListProps {
  shift: IFacilityShift;
  onWorkerClick?: (assignment: any) => void;
  onClockIn?: (assignmentId: number) => void;
  onClockOut?: (assignmentId: number) => void;
  variant?: 'detailed' | 'simple';
}

const AssignedWorkerList: React.FC<AssignedWorkerListProps> = ({ shift, onWorkerClick, onClockIn, onClockOut, variant = 'detailed' }) => {
  // Mock logic - strictly adapting from reference file logic
  // Assuming 'shift_assignment' contains worker details. 
  // If types are loose (any[]), we cast or safely access.

  const workers = shift.shift_assignment || [];

  const calculateProgress = (
    shiftStart: string,
    shiftEnd: string,
    clockIn: string | null,
    clockOut?: string | null
  ) => {
    if (!clockIn) return 0;
    const start = dayjs(shiftStart);
    const end = dayjs(shiftEnd);
    const inTime = dayjs(clockIn);
    const totalDuration = end.diff(start);
    if (totalDuration <= 0) return 0;

    const effectiveEndTime = clockOut ? dayjs(clockOut) : dayjs();
    const timeWorked = effectiveEndTime.diff(inTime);
    const percentage = (timeWorked / totalDuration) * 100;
    return Math.min(Math.max(percentage, 0), 100);
  };

  const getDurationString = (clockIn: string | null, clockOut?: string | null) => {
    if (!clockIn) return '0h 0m';
    const start = dayjs(clockIn);
    const end = clockOut ? dayjs(clockOut) : dayjs();
    const diff = end.diff(start, 'minute');
    if (diff <= 0) return '0h 0m';
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    return `${h}h ${m}m`;
  };

  const getCountdown = (startStr: string) => {
    const now = dayjs();
    const start = dayjs(startStr);
    const diff = start.diff(now, 'minute');
    if (diff <= 0) return null;

    const d = Math.floor(diff / (24 * 60));
    const h = Math.floor((diff % (24 * 60)) / 60);
    const m = diff % 60;

    // formatting similar to reference
    let res = '';
    if (d > 0) res += `${d} day${d !== 1 ? 's' : ''} `;
    if (h > 0) res += `${h} hour${h !== 1 ? 's' : ''} `;
    if (d === 0 && h === 0 && m > 0) res += `${m} minute${m !== 1 ? 's' : ''}`;
    return res.trim() || 'Starts soon';
  };

  return (
    <ListContainer>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 700 }}>
          Worker Assigned
        </Typography>
        {/* Placeholder for menu dots if needed */}
      </Box>

      {/* If no workers, maybe show message? For now assuming workers exist if this component is rendered */}

      {workers.map((assignment: any, index: number) => {
        // Defensive coding: mapping fields from 'assignment' object
        // Adjust these field accessors based on actual API response structure!
        const user = assignment.worker.user || assignment.worker.user || {};
        const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown Worker';
        const id = assignment.worker.worker_id || 'N/A';
        const avatar = user.profile_picture || '';
        const clockIn = assignment.clock_in_time; // Check specific field name
        const clockOut = assignment.clock_out_time; // Check specific field name

        const isWorkerCancelled = assignment.status === 'worker_cancelled';
        const isFacilityReject = assignment.status === 'facility_reject';
        const isLate = assignment.late_arrival || false;
        const statusLabel = isWorkerCancelled ? 'Worker Cancelled' : (isFacilityReject ? 'Facility Rejected' : (isLate ? 'Late' : 'On Time'));

        const countdown = getCountdown(shift.start_date_time);
        const isClockedIn = !!clockIn;

        if (variant === 'simple') {
          return (
            <WorkerCard
              key={index}
              onClick={() => onWorkerClick && onWorkerClick(assignment)}
              style={{ cursor: onWorkerClick ? 'pointer' : 'default', padding: '12px 16px', marginBottom: '8px' }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                <Box display="flex" alignItems="center" gap={2}>
                  <SecureAvatar src={avatar} alt={name} sx={{ width: 48, height: 48, borderRadius: '12px' }} />
                  <Box>
                    <WorkerName>{name}</WorkerName>
                    <WorkerId>{shift.position_role} • ID : {id}</WorkerId>
                  </Box>
                </Box>
                <StatusChip status={statusLabel} label={statusLabel} />
              </Box>
            </WorkerCard>
          );
        }

        return (
          <WorkerCard
            key={index}
            onClick={() => onWorkerClick && onWorkerClick(assignment)}
            style={{ cursor: onWorkerClick ? 'pointer' : 'default' }}
          >
            <WorkerHeader>
              <Box display="flex" gap={2}>
                <SecureAvatar src={avatar} alt={name} sx={{ width: 48, height: 48 }} />
                <Box>
                  <WorkerName>{name}</WorkerName>
                  <WorkerId>{shift.position_role} • ID : {id}</WorkerId>
                </Box>
              </Box>
              <StatusChip status={statusLabel} label={statusLabel} />
            </WorkerHeader>

            {!isClockedIn && countdown ? (
              <CountdownBox>
                <img src={WarningIcon} alt="warning" style={{ width: 20, height: 20 }} />
                <CountdownText>
                  Shift Starts in {countdown}
                </CountdownText>
              </CountdownBox>
            ) : (
              <>
                <Box mb={1}>
                  <DurationText>{getDurationString(clockIn, clockOut)}</DurationText>
                  <StyledLinearProgress
                    variant="determinate"
                    value={calculateProgress(shift.start_date_time, shift.end_date_time, clockIn, clockOut)}
                  />
                </Box>
                <TimeContainer>
                  <Box>
                    <TimeLabel>Clock In</TimeLabel>
                    <TimeValue>{clockIn ? dayjs(clockIn).format('HH.mm') : '-'}</TimeValue>
                    {!clockIn && onClockIn && !isWorkerCancelled && !isFacilityReject && (
                      <Typography
                        variant="caption"
                        sx={{ color: '#10B981', cursor: 'pointer', fontWeight: 600, display: 'block', mt: 0.5 }}
                        onClick={(e) => { e.stopPropagation(); onClockIn(assignment.assignment_id || assignment.id); }}
                      >
                        [ Clock In ]
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <TimeLabel>Clock Out</TimeLabel>
                    <TimeValue>{clockOut ? dayjs(clockOut).format('HH.mm') : '-'}</TimeValue>
                    {clockIn && !clockOut && onClockOut && (
                      <Typography
                        variant="caption"
                        sx={{ color: '#2563EB', cursor: 'pointer', fontWeight: 600, display: 'block', mt: 0.5 }}
                        onClick={(e) => { e.stopPropagation(); onClockOut(assignment.assignment_id || assignment.id); }}
                      >
                        [ Clock Out ]
                      </Typography>
                    )}
                  </Box>
                </TimeContainer>
              </>
            )}
          </WorkerCard>
        );
      })}


    </ListContainer>
  );
};

export default AssignedWorkerList;


